import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { formatEuro } from '../lib/calcoli'

// Saluto della dashboard concordato col genere grammaticale dell'utente.
// Se il sesso non è specificato (utenti pre-migration 016) usiamo "Ciao",
// neutro e cortese.
function salutoPerSesso(sesso) {
  if (sesso === 'M') return 'Bentornato'
  if (sesso === 'F') return 'Bentornata'
  return 'Ciao'
}

export default function Pratiche() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [pratiche, setPratiche] = useState([])
  const [loading, setLoading] = useState(true)
  const [esitoModal, setEsitoModal] = useState(null)  // { pratica, esito, motivo }
  const [processing, setProcessing] = useState(false)

  const isAdmin = profile?.ruolo === 'admin'

  useEffect(() => { fetchPratiche() }, [])

  async function fetchPratiche() {
    setLoading(true)
    // Join con profiles per mostrare il dealer che ha creato la pratica.
    // Tramite RLS l'admin riceve: tutte le SUE pratiche (qualsiasi stato)
    // + le pratiche altrui solo se stato='in_processamento'.
    const { data, error } = await supabase
      .from('pratiche')
      .select('*, dealer:profiles!pratiche_dealer_id_fkey(id, nome, cognome, email, ruolo)')
      .order('updated_at', { ascending: false })
    if (!error) setPratiche(data || [])
    setLoading(false)
  }

  async function deletePratica(id, e) {
    e.stopPropagation()
    if (!window.confirm('Eliminare definitivamente questa pratica?')) return
    await supabase.from('pratiche').delete().eq('id', id)
    fetchPratiche()
  }

  function apriModal(p, esito, e) {
    e.stopPropagation()
    setEsitoModal({ pratica: p, esito, motivo: '' })
  }

  async function confermaProcessa() {
    if (!esitoModal) return
    setProcessing(true)
    try {
      // Chiamata alla Supabase Edge Function (sostituisce /api/processa-pratica).
      // Il SDK gestisce auto URL + Bearer JWT della sessione corrente.
      const { error } = await supabase.functions.invoke('processa-pratica', {
        body: {
          pratica_id: esitoModal.pratica.id,
          esito: esitoModal.esito,
          motivo: esitoModal.motivo,
        },
      })
      if (error) {
        let msg = error.message || 'Invio fallito'
        try {
          const errBody = await error.context?.json?.()
          if (errBody?.error) msg = errBody.error
        } catch (_e) { /* ignore */ }
        alert('Errore: ' + msg)
      } else {
        setEsitoModal(null)
        fetchPratiche()
      }
    } catch (e) {
      alert('Errore di rete: ' + e.message)
    } finally {
      setProcessing(false)
    }
  }

  // Raggruppo per stato
  const inProcessamento = pratiche.filter(p => p.stato === 'in_processamento')
  const inLavorazione = pratiche.filter(p => p.stato !== 'in_processamento')

  const totaleValore = pratiche.reduce((acc, p) => acc + (Number(p.totale_finale) || 0), 0)
  const nProcess = pratiche.filter(p => p.stato === 'in_processamento').length
  const nBozze = pratiche.filter(p => p.stato !== 'in_processamento').length

  return (
    <div>
      <section className="hero-auto">
        <div className="hero-text">
          <span className="hero-eyebrow">Dashboard {isAdmin ? 'amministratore' : (profile?.ruolo === 'subagente' ? 'subagente' : 'dealer')}</span>
          <h1>{salutoPerSesso(profile?.sesso)}, <span>{profile?.nome || 'pilota'}</span></h1>
          <p>{isAdmin
            ? 'Gestisci le pratiche del network: approva, rifiuta e tieni sotto controllo le tariffe come un vero direttore di flotta.'
            : 'Crea preventivi CVT in pochi minuti e segui ogni pratica fino alla consegna. Pronti, partenza, via.'}</p>
          <div className="hero-cta">
            <button className="btn btn-primary" onClick={() => navigate('/pratiche/nuova')}>
              + Nuova pratica <span className="btn-arrow">→</span>
            </button>
            <Link to="/documentale" className="btn btn-ghost">Documentale</Link>
          </div>
          <div className="hero-kpis">
            <div className="hero-kpi"><span className="k-l">Pratiche totali</span><span className="k-v">{pratiche.length}</span></div>
            <div className="hero-kpi"><span className="k-l">In processamento</span><span className="k-v">{nProcess}</span></div>
            <div className="hero-kpi"><span className="k-l">In lavorazione</span><span className="k-v">{nBozze}</span></div>
            <div className="hero-kpi"><span className="k-l">Premio totale</span><span className="k-v">{formatEuro(totaleValore)}</span></div>
          </div>
        </div>
        <svg className="hero-car" viewBox="-20 0 500 220" aria-hidden="true">
          <defs>
            <linearGradient id="bodyGrad" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#8fd6ff"/>
              <stop offset="60%" stopColor="#19b6d9"/>
              <stop offset="100%" stopColor="#0f4fa3"/>
            </linearGradient>
            <linearGradient id="windowGrad" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#0b2545"/>
              <stop offset="100%" stopColor="#1f3b66"/>
            </linearGradient>
            <radialGradient id="headGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#fff7c2" stopOpacity="0.95"/>
              <stop offset="100%" stopColor="#fff7c2" stopOpacity="0"/>
            </radialGradient>
          </defs>
          <ellipse cx="210" cy="195" rx="170" ry="10" fill="rgba(0,0,0,0.35)"/>
          <ellipse cx="370" cy="150" rx="80" ry="40" fill="url(#headGlow)" className="head-glow"/>
          <path d="M40 165 Q60 110 130 100 L180 92 Q230 60 300 60 Q370 60 400 100 L420 130 Q420 165 410 168 L40 168 Q30 168 32 158 Z" fill="url(#bodyGrad)" stroke="#0b2545" strokeWidth="2"/>
          <path d="M150 96 Q175 70 220 68 L290 70 Q330 72 360 105 L210 105 Z" fill="url(#windowGrad)" opacity="0.92"/>
          <line x1="240" y1="70" x2="240" y2="105" stroke="#19b6d9" strokeWidth="2" opacity="0.8"/>
          <rect x="60" y="125" width="40" height="14" rx="3" fill="#0b2545" opacity="0.4"/>
          <circle cx="125" cy="170" r="28" fill="#0b2545"/>
          <circle cx="125" cy="170" r="14" fill="#1f7ae0"/>
          <circle cx="125" cy="170" r="6" fill="#fff"/>
          <circle cx="320" cy="170" r="28" fill="#0b2545"/>
          <circle cx="320" cy="170" r="14" fill="#1f7ae0"/>
          <circle cx="320" cy="170" r="6" fill="#fff"/>
          <ellipse cx="395" cy="135" rx="14" ry="8" fill="#fff7c2"/>
          <ellipse cx="50" cy="150" rx="6" ry="5" fill="#ff5e5e"/>
        </svg>
      </section>

      {loading ? (
        <div className="loading">Caricamento pratiche…</div>
      ) : pratiche.length === 0 ? (
        <div className="empty-auto">
          <svg viewBox="0 0 200 100" aria-hidden="true">
            <path d="M20 80 Q30 45 70 40 L120 36 Q150 22 175 40 L185 50 Q188 80 182 82 L20 82 Q14 82 16 76 Z" fill="currentColor" opacity="0.18" stroke="currentColor" strokeWidth="2"/>
            <circle cx="60" cy="82" r="12" fill="currentColor"/>
            <circle cx="155" cy="82" r="12" fill="currentColor"/>
            <circle cx="60" cy="82" r="5" fill="#fff"/>
            <circle cx="155" cy="82" r="5" fill="#fff"/>
          </svg>
          <h3>Garage vuoto</h3>
          <p>Non hai ancora pratiche aperte. Inizia con un nuovo preventivo CVT.</p>
          <Link to="/pratiche/nuova" className="btn btn-primary">Crea la prima pratica <span className="btn-arrow">→</span></Link>
        </div>
      ) : (
        <>
          {isAdmin && inProcessamento.length > 0 && (
            <div className="card" style={{ marginBottom: '1rem', borderColor: 'var(--purple)' }}>
              <h2 className="card-title">Da processare ({inProcessamento.length})</h2>
              <TabellaPratiche
                pratiche={inProcessamento}
                isAdmin={isAdmin}
                mostraDealer={true}
                onRow={p => navigate(`/pratiche/${p.id}`)}
                onApprova={(p, e) => apriModal(p, 'approvata', e)}
                onRifiuta={(p, e) => apriModal(p, 'rifiutata', e)}
                onDelete={deletePratica}
              />
            </div>
          )}

          {(inLavorazione.length > 0 || (!isAdmin && inProcessamento.length > 0)) && (
            <div className="card">
              <h2 className="card-title">
                {isAdmin ? 'Le mie bozze' : 'Le mie pratiche'}
              </h2>
              <TabellaPratiche
                pratiche={isAdmin ? inLavorazione : pratiche}
                isAdmin={isAdmin}
                mostraDealer={false}
                onRow={p => {
                  // Per le bozze proprie (dealer/subagente o admin con sue bozze): click → riprendi.
                  // Per le in_processamento (vista admin): dettaglio read-only.
                  const isBozza = p.stato !== 'in_processamento'
                  if (isBozza) {
                    navigate(`/pratiche/${p.id}/modifica`)
                  } else {
                    navigate(`/pratiche/${p.id}`)
                  }
                }}
                onRiprendi={p => navigate(`/pratiche/${p.id}/modifica`)}
                onDelete={deletePratica}
              />
            </div>
          )}
        </>
      )}

      {esitoModal && (
        <ModalEsito
          esitoModal={esitoModal}
          setEsitoModal={setEsitoModal}
          processing={processing}
          onConferma={confermaProcessa}
        />
      )}
    </div>
  )
}

function TabellaPratiche({ pratiche, isAdmin, mostraDealer = false, onRow, onApprova, onRifiuta, onRiprendi, onDelete }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Numero</th>
            {mostraDealer && <th>Dealer</th>}
            <th>Cliente</th>
            <th>Compagnie</th>
            <th>Totale</th>
            <th>Stato</th>
            <th>Aggiornata</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {pratiche.map(p => {
            const isBozza = p.stato !== 'in_processamento'
            const puoRiprendere = isBozza && typeof onRiprendi === 'function'
            const isAdminInProcessamento = isAdmin && p.stato === 'in_processamento'
            const dealerLabel = p.dealer
              ? `${p.dealer.cognome || ''} ${p.dealer.nome || ''}`.trim() || p.dealer.email
              : '—'
            return (
              <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => onRow(p)}>
                <td><strong>{p.numero_pratica}</strong></td>
                {mostraDealer && (
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <strong>{dealerLabel}</strong>
                      {p.dealer?.email && (
                        <span className="text-muted text-small">
                          {p.dealer.email}
                          {p.dealer.ruolo && <> · {p.dealer.ruolo}</>}
                        </span>
                      )}
                    </div>
                  </td>
                )}
                <td>{p.cliente_cognome} {p.cliente_nome}</td>
                <td>{(p.compagnie_selezionate || []).join(', ') || '—'}</td>
                <td>{formatEuro(p.totale_finale)}</td>
                <td><span className={`badge badge-${p.stato}`}>{labelStato(p.stato)}</span></td>
                <td className="text-muted text-small">{new Date(p.updated_at).toLocaleString('it-IT')}</td>
                <td onClick={e => e.stopPropagation()}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {isAdminInProcessamento && onApprova && onRifiuta && (
                      <>
                        <button className="btn btn-sm btn-teal" onClick={e => onApprova(p, e)}>Approva</button>
                        <button className="btn btn-sm btn-danger" onClick={e => onRifiuta(p, e)}>Rifiuta</button>
                      </>
                    )}
                    {puoRiprendere && (
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={e => { e.stopPropagation(); onRiprendi(p) }}
                        title="Riprendi la bozza con i dati già inseriti"
                      >
                        Riprendi
                      </button>
                    )}
                    <button className="btn btn-sm btn-danger" onClick={e => onDelete(p.id, e)} title="Elimina">×</button>
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function ModalEsito({ esitoModal, setEsitoModal, processing, onConferma }) {
  const { pratica, esito, motivo } = esitoModal
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div className="card" style={{ maxWidth: 500, width: '90%' }}>
        <h2 className="card-title">
          {esito === 'approvata' ? 'Approva pratica' : 'Rifiuta pratica'} {pratica.numero_pratica}
        </h2>
        <p className="text-small text-muted">
          Cliente: <strong>{pratica.cliente_cognome} {pratica.cliente_nome}</strong>
          <br/>Verrà inviata una mail al dealer con l'esito e la pratica sarà cancellata definitivamente.
        </p>

        <div className="form-group mt-2">
          <label>
            Motivazione {esito === 'rifiutata' && <span className="req">*</span>}
            {esito === 'approvata' && <span className="text-muted text-small"> (facoltativa)</span>}
          </label>
          <textarea
            rows={4}
            value={motivo}
            onChange={e => setEsitoModal({ ...esitoModal, motivo: e.target.value })}
            placeholder={esito === 'approvata' ? 'Note opzionali per il dealer' : 'Spiega al dealer perché la pratica è stata rifiutata'}
            style={{ width: '100%', padding: 9, border: '1px solid var(--border-strong)', borderRadius: 8 }}
          />
        </div>

        <div className="btn-row">
          <button className="btn" onClick={() => setEsitoModal(null)} disabled={processing}>Annulla</button>
          <button
            className={`btn ${esito === 'approvata' ? 'btn-teal' : 'btn-danger'}`}
            onClick={onConferma}
            disabled={processing || (esito === 'rifiutata' && !motivo.trim())}
          >
            {processing ? 'Invio...' : `Conferma ${esito === 'approvata' ? 'approvazione' : 'rifiuto'}`}
          </button>
        </div>
      </div>
    </div>
  )
}

function labelStato(s) {
  return {
    bozza: 'Bozza',
    documenti: 'Documenti',
    in_processamento: 'In processamento',
  }[s] || s
}
